import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Alert, Tag } from 'antd';
import Table from 'components/Common/Table';
import { TrophyOutlined, CloseCircleOutlined, FundOutlined, PercentageOutlined, RiseOutlined } from '@ant-design/icons';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import axios from 'axios';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const STAGE_COLORS = {
  QUALIFICATION: '#1890ff',
  NEEDS_ANALYSIS: '#722ed1',
  VALUE_PROPOSITION: '#13c2c2',
  IDENTIFY_DECISION_MAKERS: '#faad14',
  PROPOSAL_PRICE_QUOTE: '#fa8c16',
  NEGOTIATION_REVIEW: '#eb2f96',
  CLOSED_WON: '#52c41a',
  CLOSED_LOST: '#ff4d4f',
  CLOSED_LOST_TO_COMPETITION: '#cf1322',
};

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'all' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  stage: { type: 'string', default: 'all' },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const SalesPipelineReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const stageFilter = filters.stage;
  const setStageFilter = (value) => setFilters({ stage: value });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      if (stageFilter !== 'all') {
        params.stage = stageFilter;
      }

      const response = await axios.get('/deal/deals/pipeline-analytics/', { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch pipeline data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange, stageFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle preset change using helper
  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ preset: 'all', range: null });
    setStageFilter('all');
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all') count++;
    if (stageFilter !== 'all') count++;
    return count;
  };

  const columns = [
    {
      title: 'Deal #',
      dataIndex: 'deal_number',
      key: 'deal_number',
      sorter: (a, b) => (a.deal_number || '').localeCompare(b.deal_number || ''),
      width: 100,
    },
    {
      title: 'Deal Name',
      dataIndex: 'deal_name',
      key: 'deal_name',
      sorter: (a, b) => a.deal_name.localeCompare(b.deal_name),
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      width: 150,
      ellipsis: true,
      responsive: ['md'],
    },
    {
      title: 'Stage',
      dataIndex: 'stage_label',
      key: 'stage_label',
      render: (label, record) => (
        <Tag color={STAGE_COLORS[record.stage] || 'default'}>{label}</Tag>
      ),
      width: 140,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (val) => `₹${val?.toLocaleString('en-IN')}`,
      sorter: (a, b) => a.amount - b.amount,
      align: 'right',
      width: 110,
    },
    {
      title: 'Probability',
      dataIndex: 'probability',
      key: 'probability',
      render: (val) => `${val}%`,
      sorter: (a, b) => a.probability - b.probability,
      align: 'right',
      width: 90,
      responsive: ['md'],
    },
    {
      title: 'Expected Revenue',
      dataIndex: 'expected_revenue',
      key: 'expected_revenue',
      render: (val) => `₹${val?.toLocaleString('en-IN')}`,
      sorter: (a, b) => a.expected_revenue - b.expected_revenue,
      align: 'right',
      width: 130,
      responsive: ['lg'],
    },
    {
      title: 'Closing Date',
      dataIndex: 'closing_date',
      key: 'closing_date',
      sorter: (a, b) => new Date(a.closing_date) - new Date(b.closing_date),
      width: 110,
      responsive: ['md'],
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        record.is_won ? <Tag color="green">Won</Tag> :
        record.is_lost ? <Tag color="red">Lost</Tag> :
        <Tag color="blue">Open</Tag>
      ),
      width: 80,
    },
  ];

  // Stage performance table columns with responsive configuration
  const stagePerformanceColumns = [
    {
      title: 'Stage',
      dataIndex: 'stage_label',
      key: 'stage_label',
      render: (label, record) => <Tag color={STAGE_COLORS[record.stage] || 'default'}>{label}</Tag>,
      width: 140,
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      width: 70,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (v) => `₹${v?.toLocaleString('en-IN')}`,
      align: 'right',
      width: 110,
    },
    {
      title: 'Weighted Value',
      dataIndex: 'weighted',
      key: 'weighted',
      render: (v) => `₹${v?.toLocaleString('en-IN')}`,
      align: 'right',
      width: 130,
      responsive: ['md'],
    },
    {
      title: 'Avg Probability',
      dataIndex: 'avg_probability',
      key: 'avg_probability',
      render: (v) => `${v}%`,
      align: 'right',
      width: 110,
      responsive: ['lg'],
    },
  ];

  // Prepare chart data
  const stageChartData = data?.by_stage?.map(item => ({
    name: item.stage_label,
    count: item.count,
    value: item.value,
    fill: STAGE_COLORS[item.stage] || '#1890ff'
  })) || [];

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Stage</Text>
        <Select
          value={stageFilter}
          onChange={setStageFilter}
          style={{ width: '100%' }}
          placeholder="Select Stage"
        >
          <Option value="all">All Stages</Option>
          {data?.available_stages?.map(stage => (
            <Option key={stage.value} value={stage.value}>{stage.label}</Option>
          ))}
        </Select>
      </div>
    </Space>
  );

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Stage Performance',
      columns: [
        { header: 'Stage', key: 'stage_label' },
        { header: 'Count', key: 'count', align: 'right' },
        { header: 'Value', key: 'value', align: 'right', format: 'currency' },
        { header: 'Weighted Value', key: 'weighted', align: 'right', format: 'currency' },
        { header: 'Avg Probability', key: 'avg_probability', align: 'right', format: 'percent' },
      ],
      rows: data?.by_stage || [],
    },
    {
      title: 'Deal Details',
      columns: [
        { header: 'Deal #', key: 'deal_number' },
        { header: 'Deal Name', key: 'deal_name' },
        { header: 'Account', key: 'account' },
        { header: 'Stage', key: 'stage_label' },
        { header: 'Amount', key: 'amount', align: 'right', format: 'currency' },
        { header: 'Probability', key: 'probability', align: 'right', format: 'percent' },
        { header: 'Expected Revenue', key: 'expected_revenue', align: 'right', format: 'currency' },
        { header: 'Closing Date', key: 'closing_date' },
        { header: 'Owner', key: 'owner' },
      ],
      rows: data?.deals || [],
    },
  ];

  const pdfMeta = [
    { label: 'Pipeline Value', value: `Rs. ${(data?.summary?.total_value || 0).toLocaleString('en-IN')}` },
    { label: 'Deals', value: data?.summary?.total_deals || 0 },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Sales Pipeline Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Sales Pipeline Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="sales-pipeline-report"
            orientation="landscape"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <Space wrap size="middle">
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  size="small"
                  allowClear={true}
                  placeholder={['Start Date', 'End Date']}
                />
              </div>
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Stage:</Text>
                <Select
                  value={stageFilter}
                  onChange={setStageFilter}
                  size="small"
                  style={{ width: 200 }}
                >
                  <Option value="all">All Stages</Option>
                  {data?.available_stages?.map(stage => (
                    <Option key={stage.value} value={stage.value}>{stage.label}</Option>
                  ))}
                </Select>
              </div>
            </Space>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Pipeline Report Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {error && (
        <Alert message="Error" description={error} type="error" closable showIcon onClose={() => setError(null)} style={{ marginBottom: '16px' }} />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}><Text>Loading pipeline data...</Text></div>
          </div>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* Summary Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Total Deals</span>}
                  value={data.summary?.total_deals || 0}
                  prefix={<FundOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Pipeline Value</span>}
                  value={data.summary?.total_value || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Weighted Value</span>}
                  value={data.summary?.weighted_value || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Won Deals</span>}
                  value={data.summary?.won_deals || 0}
                  prefix={<TrophyOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Lost Deals</span>}
                  value={data.summary?.lost_deals || 0}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Win Rate</span>}
                  value={data.summary?.win_rate || 0}
                  suffix="%"
                  prefix={<PercentageOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: data.summary?.win_rate >= 50 ? '#52c41a' : '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Additional Stats Row */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={8} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Open Deals</span>}
                  value={data.summary?.open_deals || 0}
                  prefix={<RiseOutlined />}
                  valueStyle={{ fontSize: isMobile ? '14px' : '18px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Avg Deal Size</span>}
                  value={data.summary?.avg_deal_size || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '14px' : '18px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Avg Probability</span>}
                  value={data.summary?.avg_probability || 0}
                  suffix="%"
                  valueStyle={{ fontSize: isMobile ? '14px' : '18px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Won Value</span>}
                  value={data.summary?.won_value || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '14px' : '18px', color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            {/* Pipeline by Stage Chart */}
            <Col xs={24} md={12}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Pipeline by Stage</Text>}>
                {stageChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <BarChart
                      data={stageChartData}
                      layout="vertical"
                      margin={{
                        top: 20,
                        right: isMobile ? 10 : 30,
                        left: isMobile ? 80 : 100,
                        bottom: 5
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: isMobile ? 10 : 11 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: isMobile ? 9 : 11 }}
                        width={isMobile ? 80 : 100}
                      />
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      <Bar dataKey="value" name="Value">
                        {stageChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: isMobile ? '30px' : '40px' }}><Text type="secondary">No stage data available</Text></div>
                )}
              </Card>
            </Col>

            {/* Monthly Trend Chart */}
            <Col xs={24} md={12}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Monthly Deal Trend</Text>}>
                {data.monthly_trend && data.monthly_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <LineChart
                      data={data.monthly_trend}
                      margin={{
                        top: 20,
                        right: isMobile ? 10 : 30,
                        left: isMobile ? 0 : 20,
                        bottom: 5
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: isMobile ? 9 : 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        tick={{ fontSize: isMobile ? 10 : 11 }}
                        width={isMobile ? 50 : 60}
                      />
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      <Legend wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <Line type="monotone" dataKey="new_value" stroke="#1890ff" strokeWidth={2} name="New Pipeline" dot={{ r: isMobile ? 2 : 3 }} />
                      <Line type="monotone" dataKey="won_value" stroke="#52c41a" strokeWidth={2} name="Won" dot={{ r: isMobile ? 2 : 3 }} />
                      <Line type="monotone" dataKey="lost_value" stroke="#ff4d4f" strokeWidth={2} name="Lost" dot={{ r: isMobile ? 2 : 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: isMobile ? '30px' : '40px' }}><Text type="secondary">No trend data available</Text></div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Stage Performance Table */}
          {data.by_stage && data.by_stage.length > 0 && (
            <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Stage Performance</Text>} style={{ marginBottom: '16px' }}>
              <Table
                dataSource={data.by_stage}
                rowKey="stage"
                size="small"
                pagination={false}
                columns={stagePerformanceColumns}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          )}

          {/* Deals Detail Table */}
          <Card size="small" title={
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Deal Details</Text>
              {!isMobile && (
                <Text type="secondary" style={{ fontSize: '12px' }}>{data.summary?.date_range}</Text>
              )}
            </div>
          }>
            <Table
              columns={columns}
              dataSource={data.deals || []}
              rowKey="id"
              size="small"
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                pageSizeOptions: ['5', '10', '20', '50'],
                showSizeChanger: !isMobile,
                showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                simple: isMobile,
                style: { marginTop: '16px', marginBottom: '8px' }
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No data available. Please adjust filters.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SalesPipelineReport;
